import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Home, LayoutGrid, CalendarDays, Users, Award, Menu, X, ArrowUpRight } from "lucide-react";
import { useContent, useLang, LANGS } from "@/content";
import { Lockup } from "@/components/Brand";
import { lockScroll } from "@/lib/lenis";
import { EASE } from "@/components/Motion";

const ICONS = { Home, LayoutGrid, CalendarDays, Users, Award };
const href = (item) => (item.to ? item.to : `/#${item.hash}`);
const isActive = (item, loc) =>
  item.to ? loc.pathname === item.to : loc.pathname === "/" && loc.hash === `#${item.hash}`;

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  const c = useContent();
  return (
    <div className={`flex items-center gap-1 text-[0.7rem] tracking-[0.25em] ${className}`} data-testid="lang-selector" role="group" aria-label={c.a11y?.language}>
      {LANGS.map(({ code: l, label, name }, i) => (
        <span key={l} className="flex items-center gap-1">
          {i > 0 && <span className="text-copper/60" aria-hidden="true">/</span>}
          <button
            type="button"
            onClick={() => setLang(l)}
            aria-pressed={lang === l}
            aria-label={name}
            lang={l}
            data-testid={`lang-${l}`}
            className={`min-h-[44px] min-w-[40px] px-1.5 uppercase transition-colors duration-300 ${lang === l ? "text-copper-soft" : "text-white/85 hover:text-white"}`}
          >
            {label}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Header = ({ onOpenMenu }) => {
  const c = useContent();
  const loc = useLocation();
  if (!c.nav) return null;
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-night/95 text-white backdrop-blur-xl" data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:bg-night focus:px-4 focus:py-2" data-testid="skip-link">{c.nav.skip}</a>
      <div className="mx-auto flex h-[68px] max-w-[1480px] items-center justify-between gap-8 px-6 md:px-10">
        <Link to="/" aria-label={c.a11y?.home} data-testid="nav-logo"><Lockup /></Link>
        <nav className="hidden items-center gap-6 xl:flex" aria-label={c.a11y?.mainNav} data-testid="desktop-nav">
          {c.nav.items.map((it) => (
            <Link key={it.label} to={href(it)} data-active={isActive(it, loc)} data-testid={`nav-${(it.hash || it.to).replace(/\//g, "")}`} className="link-line whitespace-nowrap py-2 text-[0.7rem] uppercase tracking-[0.16em] text-white/90 transition-colors duration-300 hover:text-white">
              {it.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <LangToggle className="hidden md:flex" />
          <Link to={c.nav.cta.to} data-testid="nav-apply" className="group hidden items-center gap-2 rounded-[5px] bg-copper px-5 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.25em] text-white transition-opacity duration-300 hover:opacity-90 sm:inline-flex">
            {c.nav.cta.label}
            <ArrowUpRight size={14} strokeWidth={1.2} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button type="button" onClick={onOpenMenu} aria-label={c.nav.menuOpen} data-testid="menu-open-button" className="p-3 text-white xl:hidden">
            <Menu size={24} strokeWidth={1.2} />
          </button>
        </div>
      </div>
    </header>
  );
};

export const MenuSheet = ({ open, onClose }) => {
  const c = useContent();
  const loc = useLocation();
  useEffect(() => { lockScroll(open); return () => lockScroll(false); }, [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) onClose(); }, [loc.key]);
  useEffect(() => {
    if (!open) return;
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!c.nav) return null;
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div key="bd" className="fixed inset-0 z-[70] bg-night/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="menu-backdrop" />
          <motion.div
            key="sheet" role="dialog" aria-modal="true" aria-label={c.nav.menuTitle} data-testid="menu-sheet"
            className="fixed inset-x-0 bottom-0 z-[80] max-h-[92vh] overflow-y-auto rounded-t-[5px] border-t border-copper/40 bg-night px-7 pb-10 pt-4 text-white"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ duration: 0.7, ease: EASE }}
            drag="y" dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => info.offset.y > 120 && onClose()}
          >
            <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-copper/60" aria-hidden="true" />
            <div className="mb-6 flex items-center justify-between">
              <LangToggle />
              <button type="button" onClick={onClose} aria-label={c.nav.menuClose} data-testid="menu-close-button" autoFocus className="p-3"><X size={24} strokeWidth={1.2} /></button>
            </div>
            <ul className="divide-y divide-copper/15">
              {c.nav.items.map((it, i) => (
                <motion.li key={it.label} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.045, duration: 0.7, ease: EASE }}>
                  <Link to={href(it)} data-testid={`sheet-${(it.hash || it.to).replace(/\//g, "")}`} className="flex items-baseline gap-5 py-4 font-serif text-[1.65rem] leading-none">
                    <span className="w-6 font-sans text-[0.65rem] tracking-[0.2em] text-copper">{String(i + 1).padStart(2, "0")}</span>
                    {it.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <Link to={c.nav.cta.to} data-testid="sheet-apply" className="mt-8 flex items-center justify-center gap-2 rounded-[5px] bg-copper py-4 text-[0.72rem] uppercase tracking-[0.3em] text-white">
              {c.nav.cta.label} <ArrowUpRight size={15} strokeWidth={1.4} />
            </Link>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export const TabBar = () => {
  const c = useContent();
  const loc = useLocation();
  if (!c.nav) return null;
  return (
    <nav className="fixed inset-x-0 bottom-0 z-[60] border-t border-copper/25 bg-night/80 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" aria-label={c.a11y?.mobileNav} data-testid="mobile-tabbar">
      <ul className="grid grid-cols-5">
        {c.nav.tabs.map((t) => {
          const Icon = ICONS[t.icon];
          const active = t.to === "/" ? loc.pathname === "/" && !loc.hash : isActive(t, loc);
          const key = (t.hash || t.to.replace(/\//g, "") || "home");
          return (
            <li key={t.label}>
              <Link to={href(t)} data-testid={`tab-${key}`} aria-current={active ? "page" : undefined} className={`relative flex flex-col items-center gap-1 py-3 text-[0.6rem] uppercase tracking-[0.14em] transition-colors duration-300 ${active ? "text-copper" : "text-white/85"}`}>
                {active && <motion.span layoutId="tab-indicator" className="absolute inset-x-5 top-0 h-px bg-copper" transition={{ duration: 0.5, ease: EASE }} />}
                <Icon size={20} strokeWidth={1.2} />
                {t.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
